import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * useFeedback - Auto-clearing success/error feedback with timer cleanup
 *
 * Prevents memory leaks from orphaned setTimeout calls when components unmount.
 * Returns [message, showMessage, clearMessage] tuple.
 *
 * Usage:
 *   const [success, showSuccess] = useFeedback(3000);
 *   const [error, showError] = useFeedback(5000);
 *
 *   // In event handler:
 *   showSuccess('Saved successfully');
 *
 *   // In JSX:
 *   {success && <Alert severity="success">{success}</Alert>}
 */
export default function useFeedback(clearDelay = 3000) {
  const [message, setMessage] = useState('');
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const show = useCallback((msg) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMessage(msg);
    timerRef.current = setTimeout(() => {
      if (mountedRef.current) setMessage('');
    }, clearDelay);
  }, [clearDelay]);

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMessage('');
  }, []);

  return [message, show, clear];
}
